import { useContext, useState } from 'react';
import {
  ButtonIcon,
  Modal,
  Table,
  formatPhoneNumber,
  formatUnixTime,
} from '@rabbit/elements/shared-components';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { ModalNewEditCustomer } from '../../organisms/ModalNewEditCustomer/ModalNewEditCustomer';
import { useNavigate } from 'react-router-dom';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import { useGetConsumerList } from '@rabbit/bizproc/react';
import {
  Address,
  DTConsumer_Private,
  DTConsumer_Public,
  Holding,
  PersonaTypeSingleLetter,
  PrincipalsFieldName,
} from '@rabbit/data/types';
import { formatAddress, ONCE } from '../../../utils/helpers';
import { UserContext } from '../../../context/UserContext';
import { useTranslation } from 'react-i18next';
import { getRootPersonaFromLexicon } from '@rabbit/bizproc/client';
import { t } from 'i18next';
import { AppContext } from '@rabbit/app-context';
import { CORPORATE, CUSTOMER } from '@rabbit/sage/utils/consts.ts';

export interface FormValues {
  beginDate: Date | null;
  endDate: Date | null;
}

export const tableheaders = (t: any, type: string, config: any) => {
  const tenantLink = t('tenantLink');
  const showAgeRange = config?.CUSTOMER?.COMPLETE_PROFILE?.AGE_BRACKET_REQUIRED ?? false;
  const headers = [
    ...(type === 'client'
      ? [
          {
            header: t('general.businessName'),
            accessorKey: 'businessName',
            size: 150,
          },
        ]
      : []),
    {
      header: t('general.name'),
      accessorKey: 'name',
      size: 150,
    },
    {
      header: t('general.email'),
      accessorKey: 'email',
      size: 15,
    },
    {
      header: t('general.phone'),
      accessorKey: 'phone',
      size: 150,
    },
    ...(showAgeRange
      ? [
          {
            header: 'Age range',
            accessorKey: 'ageRange',
            size: 120,
          },
        ]
      : []),
    {
      header: t('general.productsRegistered'),
      accessorKey: 'productsRegistered',
      size: 20,
    },
    {
      header: t('general.lastRegistrationDate'),
      accessorKey: 'mostRecentRegistrationDate',
      size: 150,
    },
    ...(tenantLink === 'SUPAGARD'
      ? [
          {
            header: 'Unique Codes',
            accessorKey: 'uniqueCodes',
            size: 150,
          },
          {
            header: 'Leather Unique Codes',
            accessorKey: 'leatherUniqueCodes',
            size: 150,
          },
        ]
      : []),
    ...(config.CUSTOMER.SHOW_MARKETING_INFO
      ? [
          {
            header: t('general.marketing'),
            accessorKey: 'marketing',
            size: 20,
          },
        ]
      : []),
    {
      header: t('general.shippingAddress'),
      accessorKey: `shippingAddress`,
      size: 150,
      Cell: ({ cell }: any) => {
        if (
          cell.getValue() &&
          Array.isArray(cell.getValue()) &&
          Array(cell.getValue().length)
        ) {
          return (
            <div className="text-sm">{formatAddress(cell.getValue()[0])}</div>
          );
        } else return '-';
      },
      // Cell: ({
      //   row,
      // }: {
      //   row: {
      //     original: {
      //       shippingAddress: {
      //         street: string;
      //         city: string;
      //         postcode: string;
      //         country: string;
      //       };
      //     };
      //   };
      // }) => (
      //   <>
      //     <p>{row.original.shippingAddress.street}</p>
      //     <p>{row.original.shippingAddress.city}</p>
      //     <p>
      //       {row.original.shippingAddress.postcode}
      //       {row.original.shippingAddress.country}
      //     </p>
      //   </>
      // ),
    },
    ...(type === 'client'
      ? [
          {
            header: t('general.status'),
            accessorKey: 'status',
            size: 150,
          },
        ]
      : []),
    {
      header: '',
      title: 'actions',
      accessorKey: 'edit',
      size: 50,
      enableSorting: false,
      enableColumnFilter: false,
    },
  ];

  return headers;
};

export function CustomersTab({ type }: { type: string }) {
  const userContext = useContext(UserContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { config } = useContext(AppContext);
  const [isNewEditModalOpen, setIsNewEditModalOpen] = useState<boolean>(false);
  const [customerDetails, setCustomerDetails] =
    useState<DTConsumer_Private | null>(null);
  const [customerType, setCustomerType] = useState<string>('');
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>();
  const { consumerList } = useGetConsumerList(
    userContext?.user?.accessToken,
    type === 'client' ? 'corporate' : '',
    config.CUSTOMER.CORPORATE_ACCOUNT
  );
  const handleEditModal = (
    title: string,
    customerDetails: DTConsumer_Private | null,
    customerType: string = ''
  ) => {
    setModalTitle(title);
    setCustomerDetails(customerDetails);
    setIsNewEditModalOpen(true);
    setCustomerType(customerType);
  };

  const addEditModalSettings = {
    title: modalTitle,
    handleClose: () => setIsNewEditModalOpen(false),
  };

  const confirmationModalSettings = {
    kind: 'pop-up' as const,
    settings: {
      text: t('message.deleteConfirmationCustomer'),
      primaryButtonText: t('general.yes'),
      outlineButtonText: t('general.cancel'),
      handleClose: () => setIsConfirmationModalOpen(false),
      handlePrimaryClick: async () =>
        console.log('delete --> selectedRowToDelete'),
      handleOutlineClick: () => setIsConfirmationModalOpen(false),
    },
  };

  const sortData = (data: DTConsumer_Public[]) => {
    return data.sort((a: DTConsumer_Public, b: DTConsumer_Public) => {
      const aUpdate = new Date(a.tupdate);
      const bUpdate = new Date(b.tupdate);
      return bUpdate.getTime() - aUpdate.getTime();
    });
  };

  const formatResultsForCustomersTable = () => {
    const tenantLink = t('tenantLink');
    let dataList =
      consumerList && consumerList.data ? sortData(consumerList.data) : [];

    if (import.meta.env.VITE_FIREBASE_MODE === 'LIVE') {
      dataList =
        consumerList?.data?.filter(
          (result: DTConsumer_Public) =>
            result?.email &&
            !result.email?.includes('@iwarranty.co') &&
            !result?.email?.includes('@studiographene.com')
        ) ?? [];
    }

    if (consumerList.isSuccess)
      return dataList.map((result: any, i: number) => {
        return {
          id: result?.docid,
          businessName: result?.business_name ?? '',
          name:
            result?.splitname?.first && result?.splitname?.last // Check if both first and last name exist
              ? result?.splitname?.first + ' ' + result?.splitname?.last
              : result.fullname && result.fullname.trim() !== '' // Check if fullname exists and is not just whitespace
              ? result.fullname
              : '-',
          email: result?.email,
          phone: formatPhoneNumber(result?.phone),
          ageRange: result?.age_bracket ?? '-',
          productsRegistered: getNonSRVHoldings(
            result?.holdings,
            result?.srv_holdings
          ).length,
          mostRecentRegistrationDate: getMostRecentHoldingCreatedDate(
            result?.holdings,
            result?.srv_holdings
          ),
          ...(tenantLink === 'SUPAGARD'
            ? {
                uniqueCodes: getCodesFromHoldings(
                  result?.holdings,
                  'uniqueCode'
                ),
                leatherUniqueCodes: getCodesFromHoldings(
                  result?.holdings,
                  'leatherUniqueCode'
                ),
              }
            : {}),
          marketing: result?.marketing ? t('general.yes') : t('general.no'),
          shippingAddress:
            (result?.address as Address[])?.length > 1
              ? (result?.address as Address[])?.filter((i) => i.isDefault)
                  .length > 0
                ? (result?.address as Address[])?.filter((i) => i.isDefault)
                : result?.address
              : result?.address,
          status: result?.invite_status === 'completed' ? 'Active' : 'Inactive',
          edit: (
            <div className="flex justify-end gap-2 px-2 text-right">
              {/* <ButtonIcon
                label={'Edit details'}
                kind={'bgLightGreen'}
                iconLeft={true}
                Icon={PencilSquareIcon}
                onClick={() => handleEditModal('Edit customer', result)}
              /> */}
              <PencilSquareIcon
                className="text-primary-900 h-[20px] w-[20px]"
                onClick={() => {
                  handleEditModal(
                    t('general.editCustomer'),
                    result,
                    type === 'client' ? CORPORATE : CUSTOMER
                  );
                }}
              />
              {/* TODO: Delete customer is not implemented yet -VP */}
              {/* <TrashIcon
                className='w-[20px] h-[20px] text-primary-900'
                onClick={() => setIsConfirmationModalOpen(true)}
              /> */}
            </div>
          ),
        };
      });
    return dataList;
  };

  return (
    <>
      <div className="relative z-10 flex w-full items-center justify-between py-4">
        <div
          className={`relative flex w-full justify-end md:absolute md:w-1/2 ${
            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              ? 'md:top-[-10px] md:right-0'
              : 'md:top-[60px] md:right-5'
          }`}
        >
          <div>
            {type !== 'client' ? (
              <ButtonIcon
                label={t('general.newCustomer')}
                iconLeft={true}
                onClick={() =>
                  handleEditModal(
                    t('general.newCustomer'),
                    null,
                    type === 'client' ? CORPORATE : CUSTOMER
                  )
                }
              />
            ) : (
              ''
            )}
          </div>
        </div>
      </div>

      <Table
        columns={tableheaders(t, type, config)}
        data={formatResultsForCustomersTable()}
        initialState={{
          columnPinning: { right: ['actions'] },
          showGlobalFilter: true,
        }}
        enableColumnFilters={config.HOLDINGS.REGISTRATION.ADVANCED_TABLE}
        enableTopToolbar={!config.HOLDINGS.REGISTRATION.ADVANCED_TABLE}
        advancedTable={config.HOLDINGS.REGISTRATION.ADVANCED_TABLE}
        enableRowSelection={config.HOLDINGS.REGISTRATION.ADVANCED_TABLE}
        enableBottomToolbar={true}
        enableToolbarInternalActions={
          config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
        }
        enableFacetedValues={config.HOLDINGS.REGISTRATION.ADVANCED_TABLE}
        columnFilterDisplayMode="popover"
        muiSearchTextFieldProps={{
          placeholder:
            type === 'client'
              ? t('message.searchEmailAddressBusinessName')
              : t('message.searchEmailAddressCustomerName'),
          sx: {
            minWidth: '600px',
            borderRadius: '20px',
            backgroundColor: '#fff',
            margin: '5px 0',
            '& .MuiOutlinedInput-root': {
              '& > fieldset': { borderRadius: '10px' },
            },
          },
          variant: 'outlined',
          margin: 'none',
        }}
        useCallbackClickRow={(data: {
          shippingAddress: {
            street: string;
            city: string;
            postcode: string;
          };
          productsRegistered: string;
          phone: string;
          email: string;
          name: string;
          businessName: string;
          id: string;
        }) =>
          ONCE(
            () =>
              navigate(SAGE_ROUTE_NAME.MANAGE + '/customer/' + data.id, {
                state: { docid: data.id, productName: data.name },
              }),
            2000
          )()
        }
        state={{
          isLoading: consumerList.isLoading,
        }}
      />
      {isNewEditModalOpen && (
        <div>
          <ModalNewEditCustomer
            handleClose={() => setIsNewEditModalOpen(false)}
            handleDelete={() => setIsConfirmationModalOpen(true)}
            customerDetails={customerDetails}
            modalSettings={addEditModalSettings}
            customerType={customerType}
          />
          {isConfirmationModalOpen && (
            <Modal
              className="h-fit w-[350px] rounded-md bg-white"
              kind={confirmationModalSettings.kind}
              settings={confirmationModalSettings.settings}
            />
          )}
        </div>
      )}
    </>
  );
}

export default CustomersTab;

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */
const getNonSRVHoldings = (holdings: Holding[], srv_holdings: Holding[]) => {
  if (!holdings || !srv_holdings) return [];
  const manufacturer = getRootPersonaFromLexicon(
    t(PrincipalsFieldName),
    PersonaTypeSingleLetter.Manufacturer
  );
  const allHoldings = [...new Set([...holdings, ...srv_holdings])];
  const manufacturerHoldings = allHoldings.filter(
    (holding: Holding) => holding.mfr == manufacturer
  );
  return manufacturerHoldings;
};

const getMostRecentHoldingCreatedDate = (
  holdings: Holding[],
  srv_holdings: Holding[]
) => {
  if (!holdings && !srv_holdings) return '-';

  const allHoldings = [...(holdings || []), ...(srv_holdings || [])];
  if (allHoldings.length === 0) return '-';

  // After migration, holdings will have created_time field populated
  let mostRecentDate = 0;
  allHoldings.forEach((holding: any) => {
    if (holding?.created_time && holding.created_time > mostRecentDate) {
      mostRecentDate = holding.created_time;
    }
  });

  if (mostRecentDate <= 0) return '-';

  try {
    return formatUnixTime(mostRecentDate, 'dd/MM/yyyy');
  } catch (error) {
    console.error(
      'Error formatting purchase date:',
      error,
      'Date value:',
      mostRecentDate
    );
    return '-';
  }
};

const getCodesFromHoldings = (
  holdings: Holding[],
  codeKey: 'uniqueCode' | 'leatherUniqueCode'
) => {
  // Only process holdings since srv_holdings don't have uniqueCode/leatherUniqueCode
  if (!holdings) return '-';
  if (holdings.length === 0) return '-';

  // Extract codes from holdings using the specified key
  const codes = holdings
    .map((holding: any) => holding[codeKey])
    .filter((code: string | undefined) => code && code.trim() !== '');

  if (codes.length === 0) return '-';

  // Return comma-separated unique codes
  return [...new Set(codes)].join(', ');
};
